import { useEffect, useState } from 'react';

const windowLeftOffset = 0;
const windowTopOffset = 0;

/**
 * @param {string} placement - Initial position of the popup.
 * @param {React.RefObject} popRef - Reference to the popup element.
 * @param {React.RefObject} targetRef - Reference to the target element.
 * @param {boolean} isOpen - Determines when to calculate position.
 * @param {boolean} isAuto - Flag to choose between dynamic and static positioning.
 * @param {number} triggerOffset - Space between target and popup (default: 8).
 * @returns {[string, boolean]} - Current position and visibility state.
 */
export const usePosition = ({ isAuto, isOpen, placement, popRef, targetRef, triggerOffset = 8 }) => {
  const [position, setPosition] = useState(placement);
  const [showMenu, setShowMenu] = useState(false);
  const secondaryPosition = placement.split('-')[1];

  useEffect(() => {
    if (isAuto) {
      if (!targetRef.current || !popRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const popRect = popRef.current.getBoundingClientRect();

      const documentHeight = document.documentElement.clientHeight;
      const documentWidth = document.documentElement.clientWidth;

      const isMenuIntersectingWindowBottom = documentHeight < targetRect.bottom + triggerOffset + popRect.height;
      const isMenuIntersectingWindowTop = targetRect.top - triggerOffset - popRect.height < windowTopOffset;
      const isMenuIntersectingWindowLeft = targetRect.left - triggerOffset - popRect.width < windowLeftOffset;
      const isMenuIntersectingWindowRight = targetRect.right + popRect.width + triggerOffset > documentWidth;

      let newPosition = placement;

      if (isMenuIntersectingWindowTop) {
        newPosition = `bottom-${secondaryPosition}`;
        if (isMenuIntersectingWindowLeft) newPosition = 'bottom-start';
        if (isMenuIntersectingWindowRight) newPosition = 'bottom-end';
      }
      if (isMenuIntersectingWindowBottom) {
        newPosition = `top-${secondaryPosition}`;
        if (isMenuIntersectingWindowLeft) newPosition = 'top-start';
        if (isMenuIntersectingWindowRight) newPosition = 'top-end';
      }
      if (isMenuIntersectingWindowLeft) {
        newPosition = `right-${secondaryPosition}`;
        if (isMenuIntersectingWindowTop) newPosition = 'bottom-start';
        if (isMenuIntersectingWindowBottom) newPosition = 'top-start';
      }
      if (isMenuIntersectingWindowRight) {
        newPosition = `left-${secondaryPosition}`;
        if (isMenuIntersectingWindowTop) newPosition = 'bottom-end';
        if (isMenuIntersectingWindowBottom) newPosition = 'top-end';
      }

      setPosition(newPosition);
    } else {
      setPosition(placement);
    }

    setShowMenu(isOpen);
  }, [isOpen, isAuto, placement, popRef, targetRef, triggerOffset]);

  return [position, showMenu];
};
